import {
  CircularProgress,
  ClickAwayListener,
  Grow,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { ReactComponent as CheckIcon } from "components/Icons/check.svg";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field, getIn } from "formik";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import useInViewPort from "hooks/useInViewport";
import { kebabCase } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { inputStyles } from "../styles";
import { Categories, Item } from "./Categories";
import { Chips, ChipsType } from "./Chips";
import { Search } from "./Search";
import { filterElements, findValueLabel, isValueSelected } from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    inputBase: {
      background: theme.white50,
    },
    input: {
      width: ({ width }: { width?: string }) => width || "100%",
      ...inputStyles,
    },
    wrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
    },
    selected: {
      backgroundColor: theme.gray50,
    },
    item: {
      borderRadius: "8px",
      margin: theme.spacing(0, 4),
      padding: theme.spacing(4),
    },
    whiteSpace: {
      whiteSpace: "normal",
    },
    list: {
      paddingTop: 0,
    },
    itemsWrapper: {
      maxHeight: "240px",
      overflowY: "auto",
      padding: 0,
      margin: 0,
    },
    noOption: {
      color: theme.gray400,
    },
    helperText: {
      background: "transparent",
    },
    checkbox: {
      border: `3px solid ${theme.gray500}`,
      width: "18px",
      height: "18px",
      minWidth: "18px",
      borderRadius: "4px",
      marginRight: theme.spacing(8),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    checkboxChecked: {
      border: `3px solid ${theme.primary500}`,
      backgroundColor: theme.primary500,
    },
    menu: {
      zIndex: 1000,
      boxShadow:
        "0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 6px 11px 0px rgba(0, 0, 0, 0.14), 0px 3px 7px -1px rgba(0, 0, 0, 0.20)",
      maxWidth: 500,
    },
    chipsWrapper: {
      position: "absolute",
      zIndex: 1,
      left: theme.spacing(8),
      top: theme.spacing(5.5),
      fontSize: "16px",
      display: "flex",
      maxWidth: "calc(100% - 60px)",
      overflowX: "auto",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    labelWrapper: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      overflow: "hidden",
    },
    label: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      marginRight: "2rem",
      paddingRight: theme.spacing(16),
    },
    labelInfo: {
      whiteSpace: "nowrap",
      display: "inline-flex",
      alignItems: "center",
    },
    button: {
      color: theme.gray400,
      textTransform: "none",
      fontSize: 14,
      fontWeight: "normal",
      display: "flex",
      justifyContent: "left",
      "&:hover": {
        backgroundColor: "transparent",
      },
      maxWidth: "400px",
      minWidth: "200px",
      padding: theme.spacing(0, 0, 0, 6),
      "& > p": {
        textOverflow: "ellipsis",
        overflow: "hidden",
        margin: 0,
      },
    },
    circularProgress: {
      display: "flex",
      justifyContent: "center",
    },
    clearIcon: {
      cursor: "pointer",
    },
    chevronIcon: {
      zIndex: 2,
    },
  }),
);

export type SelectOption = Item;

interface Props {
  className?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  items: Array<Item>;
  label?: React.ReactNode | string;
  multiple?: boolean;
  native?: boolean;
  width?: string;
  "data-test-id"?: string;
  name: string;
  displayCategories?: boolean;
  displaySearch?: boolean;
  onChange?: (e: any, value: any) => void;
  onBlur?: (event: any) => void;
  asyncSearch?: (searchedQuery: string) => void;
  initialValue?: Item | Item[];
  placeholder?: string;
  // store value as Item type
  keepDetailedValue?: boolean;
  // keep max menu width same as dropdown
  adjustListWidthToParent?: boolean;
  renderAsChips?: boolean;
  chipsType?: ChipsType;
  size?: "medium" | "small";
  fieldType?: "button" | "textField";
  "data-hj-suppress"?: boolean;
  anyElementsToFetch?: boolean;
  loadMoreElements?: () => void;
  isCustomSelect?: boolean;
  getOptionLabel?: (value: string | string[], items: Item[]) => string;
  allowUndefinedAsItemValue?: boolean;
  adjustWidthToValue?: boolean;
  searchPlaceholder?: string;
  isClearable?: boolean;
  validateOnChange?: boolean;
}
export function SelectInput({
  "data-test-id": dataTestId,
  "data-hj-suppress": dataHjSuppress,
  adjustListWidthToParent = true,
  asyncSearch,
  chipsType = "default",
  className,
  disabled = false,
  displayCategories,
  displaySearch,
  fieldType = "textField",
  helperText,
  initialValue,
  items = [],
  keepDetailedValue,
  label,
  multiple,
  name,
  onBlur,
  onChange,
  placeholder,
  renderAsChips,
  size = "medium",
  width,
  anyElementsToFetch,
  loadMoreElements,
  isCustomSelect,
  getOptionLabel,
  allowUndefinedAsItemValue,
  adjustWidthToValue,
  searchPlaceholder,
  isClearable,
  error,
  validateOnChange,
}: Props) {
  const targetRef = useRef<HTMLDivElement>(null);
  const inViewport = useInViewPort(targetRef, { threshold: 0.5 });
  const [searchedValue, setSearchedValue] = useState("");
  const classes = useStyles({ width: width });
  const [menuWidth, setMenuWidth] = useState("100%");
  const [storedItems, setStoredItems] = useState<Item[]>([]);
  const [isOpen, setOpen] = useState(false);
  const { debouncedValue: searchQuery, isTyping } = useDebounce(searchedValue);
  const anchorRef = useRef<HTMLInputElement | HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const { message } = useMessage();

  useEffect(() => {
    if (asyncSearch) {
      asyncSearch(searchQuery);

      if (searchQuery.length === 0) {
        listRef.current?.scrollTo({ top: 0 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (inViewport && loadMoreElements) {
      loadMoreElements?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewport]);

  const toggleStoredItems = (value: string | number | Item | undefined) => {
    setStoredItems((prevItems) => {
      const itemsToStore = [...prevItems];
      const valueToCheck = keepDetailedValue ? (value as Item).id : value;
      const alreadyStoredIndex = storedItems.findIndex(
        (addedValue: Item) => addedValue.id === valueToCheck,
      );

      const isStored = alreadyStoredIndex > -1;

      if (isStored) {
        itemsToStore.splice(alreadyStoredIndex, 1);
      } else {
        const newDetailedItem = keepDetailedValue
          ? value
          : (items.find((item) => item.id === value) as Item);

        itemsToStore.push(newDetailedItem as Item);
      }

      return itemsToStore;
    });
  };

  return (
    <div
      className={`${classes.input} ${className}`}
      data-hj-suppress={dataHjSuppress}
    >
      <Field name={name}>
        {({ field, form, meta }: { field: any; form: any; meta: any }) => {
          const isError = meta.error || error || getIn(form.status, field.name);

          const setValue = (value: string | number | Item | undefined) => {
            if (onChange) {
              onChange({ target: { value: value } }, value);
            }

            if (multiple) {
              if (asyncSearch) {
                // store selected value from async api call
                toggleStoredItems(value);
              }
              const isValueAlreadyAdded =
                field.value &&
                field.value?.findIndex?.((addedValue: string | Item) =>
                  keepDetailedValue
                    ? (addedValue as Item).id === (value as Item).id
                    : addedValue === value,
                ) !== -1;

              if (isValueAlreadyAdded) {
                const valuesToSave =
                  Array.isArray(field.value) &&
                  [...field.value]?.filter((item) =>
                    keepDetailedValue
                      ? item.id !== (value as Item).id
                      : item !== value,
                  );

                form.setFieldValue(field.name, valuesToSave, validateOnChange);
              } else {
                // Check missing initialValue
                if (field.value) {
                  return form.setFieldValue(
                    field.name,
                    [...field.value, value],
                    validateOnChange,
                  );
                }

                form.setFieldValue(field.name, [value], validateOnChange);
              }
            } else {
              !isCustomSelect &&
                form.setFieldValue(field.name, value, validateOnChange);
              setOpen(false);
            }
          };

          const getLabelValue = (value: string | string[] | Item) =>
            findValueLabel(
              [...items, ...storedItems],
              value,
              initialValue,
              keepDetailedValue,
              allowUndefinedAsItemValue,
            );
          const valueLabel = getOptionLabel
            ? getOptionLabel(field.value, items)
            : getLabelValue(field.value);
          const allowedConditions = items.filter((item) => !item.isDeprecated);
          const filteredItems = filterElements(allowedConditions, searchQuery);
          const isAnyOption = filteredItems.length > 0;

          return (
            <div className={classes.wrapper}>
              {renderAsChips && field.value && (
                <div className={classes.chipsWrapper}>
                  {Array.isArray(field.value) ? (
                    field.value?.map((value: string | Item) => {
                      const id = typeof value === "string" ? value : value.id;
                      const label = getLabelValue(value);

                      return (
                        <Chips
                          key={id}
                          onRemoveClick={() =>
                            setValue(keepDetailedValue ? { id, label } : id)
                          }
                          label={label}
                          type={chipsType}
                          dataTestId={`${dataTestId}-select-chips-${id}`}
                        />
                      );
                    })
                  ) : (
                    <Chips
                      label={getLabelValue(field.value)}
                      type={chipsType}
                      onRemoveClick={() =>
                        setValue(
                          keepDetailedValue
                            ? {
                                id: field.value,
                                label: getLabelValue(field.value),
                              }
                            : field.value,
                        )
                      }
                      dataTestId={`${dataTestId}-chips`}
                    />
                  )}
                </div>
              )}
              {fieldType === "button" && (
                <div ref={anchorRef}>
                  <OLButton
                    className={classes.button}
                    onClick={(event) => {
                      setOpen(!isOpen);
                      adjustListWidthToParent &&
                        setMenuWidth(event.currentTarget.offsetWidth + "px");
                    }}
                    data-test-id={`${dataTestId || field.name}-select-wrapper`}
                    endIcon={<ChevronDownIcon />}
                  >
                    <p>{valueLabel}</p>
                  </OLButton>
                </div>
              )}
              {fieldType === "textField" && (
                <TextField
                  disabled={disabled}
                  size={size}
                  ref={anchorRef}
                  className={classes.input}
                  placeholder={placeholder}
                  InputLabelProps={{
                    shrink:
                      isOpen || !!field.value || allowUndefinedAsItemValue,
                  }}
                  label={label}
                  inputProps={{
                    autoComplete: "off",
                    readOnly: true,
                    "data-test-id": dataTestId
                      ? dataTestId
                      : `${field.name}-input`,
                  }}
                  value={valueLabel}
                  onClick={(event) => {
                    !disabled && setOpen(!isOpen);
                    adjustListWidthToParent &&
                      setMenuWidth(event.currentTarget.offsetWidth + "px");
                  }}
                  sx={{
                    "& label.MuiInputLabel-shrink": {
                      paddingRight: "0 !important",
                    },
                    label: {
                      paddingRight: "28px !important",
                    },
                    input: {
                      // autoCalc input width based on value length
                      width: adjustWidthToValue
                        ? (valueLabel.length + 1) / 2 + "em"
                        : "inherit",
                      color:
                        renderAsChips && valueLabel.length > 0
                          ? "transparent"
                          : "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  fullWidth
                  onBlur={onBlur}
                  helperText={isError ? Object.values(isError) : helperText}
                  error={isError}
                  InputProps={{
                    className: classes.inputBase,
                    endAdornment: (
                      <InputAdornment position="end">
                        {isClearable && field.value && (
                          <XCloseIcon
                            className={classes.clearIcon}
                            onClick={(e) => {
                              e.stopPropagation();
                              form.setFieldValue(
                                field.name,
                                undefined,
                                validateOnChange,
                              );
                            }}
                          />
                        )}
                        <ChevronDownIcon className={classes.chevronIcon} />
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{
                    className: classes.helperText,
                  }}
                  data-test-id={`${dataTestId || field.name}-select-wrapper`}
                />
              )}
              <Popper
                open={isOpen}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                transition
                disablePortal
                sx={{
                  zIndex: 1302,
                }}
                className="select-dropdown"
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === "bottom-start"
                          ? "left top"
                          : "left bottom",
                    }}
                  >
                    <Paper>
                      <ClickAwayListener
                        onClickAway={(event) => {
                          if (
                            (event.target as HTMLElement)?.dataset.role ===
                              "chips" ||
                            (event.target as HTMLElement)?.dataset.role ===
                              "close"
                          )
                            return;
                          setSearchedValue("");
                          setOpen(false);
                        }}
                      >
                        <MenuList
                          sx={{
                            minWidth: anchorRef?.current?.offsetWidth || "100%",
                            maxWidth: menuWidth,
                            zIndex: 1302,
                          }}
                          className={`${classes.menu} ${
                            displaySearch ? classes.list : ""
                          }`}
                        >
                          {displayCategories ? (
                            <Categories
                              data-test-id={dataTestId}
                              displaySearch={displaySearch}
                              isTypingGlobal={isTyping}
                              items={filteredItems}
                              multiple={multiple}
                              searchedValue={searchedValue}
                              setSearchedValue={setSearchedValue}
                              setValue={setValue}
                              value={field.value}
                            />
                          ) : (
                            <div>
                              {displaySearch && (
                                <Search
                                  searchedValue={searchedValue}
                                  setSearchedValue={setSearchedValue}
                                  isLoading={isTyping}
                                  data-test-id={dataTestId}
                                  placeholder={searchPlaceholder}
                                />
                              )}
                              <ul
                                role="listbox"
                                data-name={field.name}
                                className={classes.itemsWrapper}
                                data-test-id={`${dataTestId}-select-list`}
                                ref={listRef}
                              >
                                {isAnyOption ? (
                                  filteredItems.map(
                                    (item: Item, index: number) => {
                                      const isSelected = isValueSelected(
                                        item,
                                        field.value,
                                        keepDetailedValue,
                                      );

                                      return (
                                        <MenuItem
                                          className={`${classes.item} ${
                                            isSelected ? classes.selected : ""
                                          } ${
                                            fieldType === "textField"
                                              ? classes.whiteSpace
                                              : ""
                                          }`}
                                          disabled={item.disabled}
                                          key={item.id}
                                          onClick={() => {
                                            setValue(
                                              keepDetailedValue
                                                ? item
                                                : item.id,
                                            );
                                          }}
                                          data-test-id={`${
                                            dataTestId || field.name
                                          }-list-item-${
                                            allowUndefinedAsItemValue
                                              ? undefined
                                              : kebabCase(item.id as string)
                                          }`}
                                          tabIndex={index}
                                          id={item.id as string}
                                          data-hj-suppress={dataHjSuppress}
                                        >
                                          {multiple && (
                                            <div
                                              className={`${classes.checkbox} ${
                                                isSelected &&
                                                classes.checkboxChecked
                                              }`}
                                            >
                                              {isSelected && (
                                                <CheckIcon color="white" />
                                              )}
                                            </div>
                                          )}
                                          <div className={classes.labelWrapper}>
                                            <span className={classes.label}>
                                              {item.label}
                                            </span>
                                            {item.labelInfo && (
                                              <span
                                                className={classes.labelInfo}
                                              >
                                                {item.labelInfo}
                                              </span>
                                            )}
                                          </div>
                                        </MenuItem>
                                      );
                                    },
                                  )
                                ) : (
                                  <MenuItem className={classes.noOption}>
                                    {message({ id: "noOptions" })}
                                  </MenuItem>
                                )}
                                {anyElementsToFetch && (
                                  <div
                                    ref={targetRef}
                                    className={classes.circularProgress}
                                  >
                                    <CircularProgress size={20} />
                                  </div>
                                )}
                              </ul>
                            </div>
                          )}
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </div>
          );
        }}
      </Field>
    </div>
  );
}
